import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { Sex, SmokingStatus } from '@biosensesignal/web-sdk';
import { MeasurementResults } from '../hooks/useResultsExport';
import ExportButton from './ExportButton';
import Logo from './Logo';
import FooterLogo from './FooterLogo';
import { METRICS, MetricId, SNAPSHOT_METRICS, getMetricScore, availableIndicatorCount } from './health/metricConfig';
import { remapForTwoBands } from './health/MetricGauge';
import MetricRow from './health/MetricRow';
import { CE_GROUPS, isIndicatorEnabled } from './health/ceIndicatorGroups';
import CeIndicatorRow from './health/CeIndicatorRow';
import CeVitalGaugeRow from './health/CeVitalGaugeRow';
import WellnessArc from './health/WellnessArc';
import MetricDeck from './health/MetricDeck';
import JourneyPanel from './health/JourneyPanel';
import { computeJourney } from './health/glp1Indicators';
import { glp1Active } from './health/glp1Config';
import { TherapyProfile, CheckIn } from '../types/glp1';
import DetailBlocks from './health/DetailBlocks';
import { buildMetricBlocks } from './health/metricDetailBlocks';
import { colors, fontSize, fontWeight, spacing, buttonDefaults } from '../style/tokens';
import { useTheme } from 'styled-components';
import { headerBg } from '../style/header';
import { reportFileName } from '../utils/reportFileName';
import { buildReportPayload, buildCeReportPayload, reportMetrics, overallReportScore } from '../utils/buildReportPayload';

// ── Layout ────────────────────────────────────────────────────────────────────

const Page = styled.div<{ $sfondo?: string | null }>`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${p => p.$sfondo || colors.pageBg};
  font-family: 'Outfit', sans-serif;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px 26px;
  min-height: 82px;
  background: ${headerBg};
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  flex-shrink: 0;
`;

const LogoCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

const TopBarSpacer = styled.span`width: 42px;`;

const ReportHeader = styled.div`
  padding: ${spacing.xl2} ${spacing.xl} ${spacing.lg};
  background: transparent;
  margin-bottom: 10px;
`;

const ReportTitle = styled.h1`
  font-size: 30px;
  font-weight: ${fontWeight.extrabold};
  color: ${colors.text};
  line-height: 1.2;
  margin-bottom: ${spacing.xs};
`;

const ReportDate = styled.p`
  font-size: ${fontSize.md};
  color: ${colors.muted};
`;

const ScoreBar = styled.div`
  margin: 0 14px 20px;
  background: ${colors.bg};
  border: 1.5px solid #e8e8e8;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: box-shadow 0.15s;
  @media (hover: hover) { &:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.08); } }
`;

const ScoreInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScoreBarLabel = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const ScoreBarValue = styled.div`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.extrabold};
  color: ${colors.navy};
  line-height: 1.2;
`;

const ScoreBarCount = styled.div`
  font-size: ${fontSize.xs};
  color: ${colors.muted};
  margin-top: 4px;
`;

const Section = styled.div`
  padding: 0 14px;
  margin-bottom: ${spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.gradient1};
  margin-bottom: 10px;
  padding: 0 2px;
`;

const InfoCard = styled.div`
  background: ${colors.bg};
  border: 1.5px solid ${p => p.theme.gradient1};
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 10px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
`;

const LowConfNote = styled.p`
  font-size: ${fontSize.xs};
  color: ${colors.muted};
  margin: 6px 2px 0;
  line-height: 1.4;
`;

const InfoLabel = styled.span`
  font-size: ${fontSize.base};
  color: ${colors.muted};
  font-weight: ${fontWeight.medium};
`;

const InfoValue = styled.span`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.bold};
  color: ${colors.text};
`;

const InfoUnit = styled.span`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.normal};
  color: ${colors.muted};
  margin-left: 3px;
`;

const Disclaimer = styled.p`
  font-size: ${fontSize.xs};
  color: #aaa;
  text-align: center;
  padding: 0 ${spacing.xl} ${spacing.xl};
  line-height: 1.55;
`;

const BtnDownloadWide = styled.button`
  display: block;
  width: 100%;
  padding: ${spacing.lg} 0;
  border-radius: 50px;
  border: none;
  background: ${p => p.theme.coloreBottoneInattivo || buttonDefaults.inactive};
  color: #fff;
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  font-family: 'Outfit', sans-serif;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  @media (hover: hover) { &:hover { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; } }
  &:active { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; transform: scale(0.98); }
  &:disabled { background: ${p => p.theme.coloreBottoneDisabilitato || buttonDefaults.inactive}; opacity: 0.7; cursor: default; }
`;

const BtnRow = styled.div`
  padding: 8px 14px 24px;
  display: flex;
  gap: 10px;
`;

const BtnGhost = styled.button`
  flex: 1;
  padding: 15px 0;
  border-radius: 50px;
  border: 1.5px solid #ddd;
  background: ${colors.bg};
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  font-family: 'Outfit', sans-serif;
  color: #555;
  cursor: pointer;
  transition: background 0.15s;
  @media (hover: hover) { &:hover { background: #f9f9f9; } }
`;

const DevJsonButton = styled.button`
  display: block;
  margin: 8px auto 16px;
  background: none;
  border: 1px dashed #ccc;
  color: #bbb;
  font-size: ${fontSize.xs};
  font-family: 'Outfit', sans-serif;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  @media (hover: hover) { &:hover { color: #888; border-color: #aaa; } }
`;

const SAFE_CTA_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];
function safeCTAUrl(url: string): string {
  try {
    return SAFE_CTA_SCHEMES.includes(new URL(url).protocol) ? url : '#';
  } catch {
    return '#';
  }
}

const BtnCta = styled.a`
  flex: 1;
  padding: 15px 0;
  border-radius: 50px;
  border: none;
  background: ${p => p.theme.coloreBottoneInattivo || buttonDefaults.inactive};
  color: #fff;
  font-size: 15px;
  font-weight: ${fontWeight.bold};
  font-family: 'Outfit', sans-serif;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, transform 0.1s;
  @media (hover: hover) { &:hover { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; } }
  &:active { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; transform: scale(0.98); }
`;

const NoData = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.muted};
  font-size: ${fontSize.lg};
`;

// ── Overall score helpers ─────────────────────────────────────────────────────
// overallAvgScore lives in buildReportPayload.ts so the score shown here and the
// one delivered to a partner can never drift apart.

function overallBannerKey(avg: number): string {
  if (avg >= 75) return 'report.bannerGood';
  if (avg >= 50) return 'report.bannerModerate';
  return 'report.bannerReview';
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ReportPageProps {
  results: MeasurementResults | null;
  onMeasureAgain: () => void;
  onDownload: () => void;
  isDownloading?: boolean;
  onSettingsClick: () => void;
  onMetricClick: (id: MetricId) => void;
  /** CE only: open the detail page for a tapped indicator (matched by resultCode). */
  onCeIndicatorClick?: (code: string) => void;
  /**
   * The partner's results page for this session, returned by their report
   * callback (data_return_url + data_return_parameters). When present it takes
   * over the CTA, because it points at *this user's* results rather than the
   * static marketing link configured in branding.
   */
  dataReturnUrl?: string | null;
  /**
   * GLP-1 journey context confirmed by the user this session (§3.1.1). Null when
   * the step was skipped or the user is not on therapy — the Journey panel then
   * does not render.
   */
  journeyProfile?: TherapyProfile | null;
  journeyCheckIn?: CheckIn | null;
  /** §4.12 optional severe-symptom answer; escalates on its own. */
  severeSymptom?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ReportPage: React.FC<ReportPageProps> = ({
  results,
  onMeasureAgain,
  onDownload,
  isDownloading = false,
  onSettingsClick,
  onMetricClick,
  onCeIndicatorClick,
  dataReturnUrl = null,
  journeyProfile = null,
  journeyCheckIn = null,
  severeSymptom = false,
}) => {
  const intl = useIntl();

  if (!results) {
    return (
      <Page>
        <TopBar>
          <TopBarSpacer />
          <LogoCenter><Logo /></LogoCenter>
          <TopBarSpacer />
        </TopBar>
        <NoData>{intl.formatMessage({ id: 'report.noData' })}</NoData>
        <FooterLogo />
      </Page>
    );
  }

  const dem = results.demographics;

  const theme = useTheme();
  const { enabledMetrics, reportButton, sfondo, resultBands, istanza_ce } = theme;
  const visibleMetrics = reportMetrics(enabledMetrics);

  // A partner whose enabled_metrics leaves a single indicator (the Burnout-only
  // case) has nowhere to drill into: its detail copy is rendered inline below
  // the card instead, and the card stops being tappable. Wellness only — CE
  // indicators are gated by the Binah license, not by branding.
  const singleMetric = !istanza_ce && visibleMetrics.length === 1
    ? visibleMetrics[0]
    : null;

  // The downloaded JSON is the same document delivered to a partner's Report
  // Endpoint (see useReportDelivery) — one builder, so they can't diverge. A CE
  // instance reports the clinical indicators shown above, not wellness scores.
  const handleDownloadJson = React.useCallback(() => {
    const payload = istanza_ce
      ? buildCeReportPayload(results, intl)
      : buildReportPayload(results, visibleMetrics, intl);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = reportFileName(istanza_ce, results.timestamp, 'json');
    a.click();
    URL.revokeObjectURL(blobUrl);
  }, [results, visibleMetrics, intl, istanza_ce]);

  // The partner's per-session results page wins over the static branding link:
  // it points at this user's report, which is the whole point of the callback.
  const ctaUrl = dataReturnUrl ?? reportButton.url;

  // GLP-1 Journey panel (§4.11–§4.13). Rendered only when BOTH the tenant flag
  // and the user's self-declared therapy status hold (§2) — glp1Active owns that
  // rule, so it can never be half-checked here. A skipped onboarding step leaves
  // journeyProfile null, and the panel does not render at all.
  const journey =
    journeyProfile && journeyCheckIn && glp1Active(theme, journeyProfile)
      ? computeJourney(journeyProfile, journeyCheckIn, dem?.weight, {
          hr: results.vitalSigns.pulseRate,
          // Stateless: no cross-session history, so no personal baseline and the
          // HR cue stays disabled — conforming per ruling B6.
          baselineHr: null,
          severeSymptom,
        })
      : null;

  const avgScore = overallReportScore(results);
  const remappedAvg = resultBands === 2 ? remapForTwoBands(avgScore) : avgScore;
  const markerLeft = Math.max(4, Math.min(96, remappedAvg));

  return (
    <Page $sfondo={sfondo}>
      <TopBar>
        <TopBarSpacer />
        <LogoCenter><Logo /></LogoCenter>
        <TopBarSpacer />
      </TopBar>

      <ReportHeader>
        <ReportTitle style={istanza_ce ? { fontSize: '20px', textAlign: 'center' } : undefined}>
          {intl.formatMessage({ id: istanza_ce ? 'report.measurementResults' : 'report.wellnessSnapshot' })}
        </ReportTitle>
        <ReportDate style={istanza_ce ? { textAlign: 'center' } : undefined}>
          {results.sessionInfo.measurementDate} · {results.sessionInfo.measurementTime}
        </ReportDate>
      </ReportHeader>

      {/* The overall score averages the snapshot indicators, so it says nothing
          about a single-indicator instance — and for a Burnout-only partner it
          would be computed entirely from indicators the report never shows. */}
      {!istanza_ce && !singleMetric && (
      <ScoreBar onClick={() => onMetricClick('wellnessSnapshot')}>
        <WellnessArc position={markerLeft} bands={resultBands} />
        <ScoreInfo>
          <ScoreBarLabel>{intl.formatMessage({ id: 'report.overallWellnessScore' })}</ScoreBarLabel>
          <ScoreBarValue>{intl.formatMessage({ id: overallBannerKey(avgScore) })}</ScoreBarValue>
          <ScoreBarCount>
            {intl.formatMessage(
              { id: 'report.indicatorsUsed' },
              availableIndicatorCount(results, dem),
            )}
          </ScoreBarCount>
        </ScoreInfo>
      </ScoreBar>
)}
      {/* Demographics — temporarily hidden */}
      {/* {dem && (
        <Section>
          <SectionTitle g1={gradient1}>{intl.formatMessage({ id: 'report.demographics' })}</SectionTitle>
          <InfoCard g1={gradient1}>
            <InfoRow>
              <InfoLabel>{intl.formatMessage({ id: 'form.sex' })}</InfoLabel>
              <InfoValue>
                {dem.sex === Sex.MALE
                  ? intl.formatMessage({ id: 'form.male' })
                  : intl.formatMessage({ id: 'form.female' })}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{intl.formatMessage({ id: 'form.age' })}</InfoLabel>
              <InfoValue>{dem.age} <InfoUnit>{intl.formatMessage({ id: 'report.years' })}</InfoUnit></InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{intl.formatMessage({ id: 'form.weight' })}</InfoLabel>
              <InfoValue>{dem.weight} <InfoUnit>kg</InfoUnit></InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{intl.formatMessage({ id: 'form.height' })}</InfoLabel>
              <InfoValue>{dem.height} <InfoUnit>cm</InfoUnit></InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{intl.formatMessage({ id: 'form.smokingStatus' })}</InfoLabel>
              <InfoValue>
                {dem.smokingStatus === SmokingStatus.SMOKER
                  ? intl.formatMessage({ id: 'form.smoker' })
                  : intl.formatMessage({ id: 'form.nonSmoker' })}
              </InfoValue>
            </InfoRow>
          </InfoCard>
        </Section>
      )} */}


      {/* if CE, check report, else Wellness metrics */}
      {istanza_ce ? (
        CE_GROUPS.map(group => {
          // License-disabled indicators are never produced on any scan → drop them.
          const enabledIndicators = group.indicators.filter(ind =>
            isIndicatorEnabled(ind, results.enabled),
          );
          // A group with no licensed indicators is not rendered at all.
          if (enabledIndicators.length === 0) return null;
          const vs = results.vitalSigns;
          // A "*" is shown when a displayed reading is below the confidence gate;
          // the footnote is rendered once per group if any row is flagged.
          const groupHasLowConf = enabledIndicators.some(
            ind => ind.isAvailable(vs) && ind.isLowConfidence?.(vs),
          );
          return (
          <Section key={group.titleKey}>
            <SectionTitle>{intl.formatMessage({ id: group.titleKey })}</SectionTitle>
            <InfoCard>
              {enabledIndicators.map(ind => {
                const { display, risk } = ind.getValue(vs);
                const label = intl.formatMessage({ id: ind.labelKey });
                const available = ind.isAvailable(vs);
                const openDetail = onCeIndicatorClick
                  ? () => onCeIndicatorClick(ind.resultCode)
                  : undefined;
                if (ind.getScore) {
                  return (
                    <CeVitalGaugeRow
                      key={ind.labelKey}
                      label={label}
                      value={display}
                      score={ind.getScore(vs, dem)}
                      bands={group.bands}
                      hideValue={ind.hideValue}
                      unavailable={!available}
                      lowConfidence={available && !!ind.isLowConfidence?.(vs)}
                      invertGaugeLabels={ind.invertGaugeLabels}
                      onClick={openDetail}
                    />
                  );
                }
                return (
                  <CeIndicatorRow
                    key={ind.labelKey}
                    label={label}
                    value={display}
                    risk={risk}
                    onClick={openDetail}
                  />
                );
              })}
            </InfoCard>
            {groupHasLowConf && (
              <LowConfNote>{intl.formatMessage({ id: 'ce.lowConfidenceNote' })}</LowConfNote>
            )}
          </Section>
          );
        })
      ) : (
        <>
          <MetricDeck
            metrics={visibleMetrics}
            results={results}
            demographics={dem}
            onMetricClick={onMetricClick}
          />
          {singleMetric && (
            <DetailBlocks
              blocks={buildMetricBlocks(
                singleMetric,
                results,
                getMetricScore(singleMetric, results, dem),
                intl,
              )}
              // The report page already ends with report.reportDisclaimer.
              showDisclaimer={false}
            />
          )}
        </>
      )}
      
      

      {journey && <JourneyPanel journey={journey} />}

      {!istanza_ce && (
        <Disclaimer>{intl.formatMessage({ id: 'report.reportDisclaimer' })}</Disclaimer>
      )}

      <BtnRow>
        <BtnDownloadWide onClick={onDownload} disabled={isDownloading}>
          {isDownloading
            ? intl.formatMessage({ id: 'report.generatingPdf' })
            : intl.formatMessage({ id: 'report.startConsultation' })}
        </BtnDownloadWide>
      </BtnRow>

      <BtnRow>
        <BtnGhost onClick={onMeasureAgain}>{intl.formatMessage({ id: 'report.repeatScan' })}</BtnGhost>
        {reportButton.visible && ctaUrl && (
          <BtnCta
            href={safeCTAUrl(ctaUrl)}
            // A per-session results page belongs in this tab — sending the user
            // to their own report in a popup would be lost behind the scan tab.
            target={dataReturnUrl ? undefined : (reportButton.target ?? undefined)}
            rel={!dataReturnUrl && reportButton.target === '_blank' ? 'noopener noreferrer' : undefined}
          >
            {dataReturnUrl
              ? intl.formatMessage({ id: 'report.viewMyResults' })
              : reportButton.label ?? intl.formatMessage({ id: 'report.startConsultation' })}
          </BtnCta>
        )}
      </BtnRow>

      {process.env.NODE_ENV === 'development' && (
        <DevJsonButton onClick={handleDownloadJson}>[dev] download JSON report</DevJsonButton>
      )}

      <FooterLogo />
    </Page>
  );
};

export default ReportPage;
